// Script Graph - Non-linear script flow system
// This replaces the linear getStageConfig function with a graph-based approach

export const scriptGraph = {
  nodes: {
    'opening': {
      id: 'opening',
      script: 'Hi, I\'m calling about your property on [Prospect Address]. Have you ever considered selling your home?',
      coaching: 'Start with a simple, direct question about selling interest.',
      responses: {
        'yes': { 
          nextNode: 'timeline', 
          data: { openToSelling: true },
          label: 'Yes, I\'m interested'
        },
        'no': { 
          nextNode: 'closing', 
          data: { openToSelling: false },
          label: 'No, not interested'
        }
      },
      requiresTextInput: false,
      step: 'interest', // Add step property for objection responses
      aiConfig: {
        canGenerateCustomScript: true,
        canAddCustomResponses: true,
        canSkipToAnyNode: false,
        contextWindow: 5,
        fallbackNode: 'closing'
      }
    },

    'timeline': {
      id: 'timeline',
      script: 'Great! What\'s your timeline for selling?',
      coaching: 'Determine their urgency and timeline.',
      responses: {
        'immediate': { 
          nextNode: 'closing_immediate', 
          data: { timeline: 'Immediate' },
          label: 'I want to sell immediately'
        },
        'within_6_months': { 
          nextNode: 'closing_followup', 
          data: { timeline: '6 Months' },
          label: 'Within 6 months'
        },
        'not_sure': { 
          nextNode: 'closing_nurture', 
          data: { timeline: 'Unsure' },
          label: 'Not sure yet'
        }
      },
      requiresTextInput: false,
      step: 'motivation', // Add step property for objection responses
      aiConfig: {
        canGenerateCustomScript: true,
        canAddCustomResponses: true,
        canSkipToAnyNode: false,
        contextWindow: 10,
        fallbackNode: 'closing_followup'
      }
    },

    'closing_immediate': {
      id: 'closing_immediate',
      script: 'Perfect! I\'d love to help you get the best price for your home. Can I schedule a time to come by and give you a free market analysis?',
      coaching: 'High priority - they want to sell immediately. Push for appointment.',
      responses: {
        'schedule': { 
          nextNode: 'closing_notes', 
          data: { appointmentRequested: true },
          label: 'Yes, let\'s schedule it'
        },
        'later': { 
          nextNode: 'closing_followup', 
          data: { appointmentRequested: false },
          label: 'Maybe later'
        }
      },
      requiresTextInput: false,
      step: 'condition', // Add step property for objection responses
      aiConfig: {
        canGenerateCustomScript: true,
        canAddCustomResponses: true,
        canSkipToAnyNode: false,
        contextWindow: 15,
        fallbackNode: 'closing_followup'
      }
    },

    'closing_followup': {
      id: 'closing_followup',
      script: 'I understand timing is important. Would you like me to keep you updated on market trends in your area?',
      coaching: 'Medium priority - keep them in the system for follow-up.',
      responses: {
        'yes_updates': { 
          nextNode: 'closing_notes', 
          data: { followUpAction: 'Email Updates' },
          label: 'Yes, keep me updated'
        },
        'no_updates': { 
          nextNode: 'closing_notes', 
          data: { followUpAction: 'None' },
          label: 'No thanks'
        }
      },
      requiresTextInput: false,
      step: 'condition', // Add step property for objection responses
      aiConfig: {
        canGenerateCustomScript: true,
        canAddCustomResponses: true,
        canSkipToAnyNode: false,
        contextWindow: 15,
        fallbackNode: 'closing_notes'
      }
    },

    'closing_nurture': {
      id: 'closing_nurture',
      script: 'I appreciate you sharing your thoughts. A lot of your neighbors have asked me to keep them updated on market trends. Would you like me to include you in those updates?',
      coaching: 'Low priority - add to long-term nurture campaign.',
      responses: {
        'yes_nurture': { 
          nextNode: 'closing_notes', 
          data: { followUpAction: 'Nurture Campaign' },
          label: 'Yes, include me'
        },
        'no_nurture': { 
          nextNode: 'closing_notes', 
          data: { followUpAction: 'None' },
          label: 'No thanks'
        }
      },
      requiresTextInput: false,
      step: 'condition', // Add step property for objection responses
      aiConfig: {
        canGenerateCustomScript: true,
        canAddCustomResponses: true,
        canSkipToAnyNode: false,
        contextWindow: 15,
        fallbackNode: 'closing_notes'
      }
    },

    'closing_notes': {
      id: 'closing_notes',
      script: 'Thank you for your time! Is there anything else I should know about your situation?',
      coaching: 'Optional: Capture any additional context for follow-up.',
      responses: {
        'submit_notes': { 
          nextNode: 'END_SCRIPT', 
          data: {},
          label: 'Add Closing Notes',
          type: 'open_text',
          placeholder: 'Add any additional notes...',
          dataField: 'additionalNotes'
        }
      },
      requiresTextInput: true,
      step: 'condition', // Add step property for objection responses
      aiConfig: {
        canGenerateCustomScript: false,
        canAddCustomResponses: false,
        canSkipToAnyNode: false,
        contextWindow: 20,
        fallbackNode: 'call_end'
      }
    },

    'closing': {
      id: 'closing',
      script: 'I understand, and I appreciate your time. If you change your mind or know anyone who might be interested, feel free to reach out.',
      coaching: 'Polite closing for uninterested prospects.',
      responses: {
        'end_call': { 
          nextNode: '__end_script__', 
          data: {},
          label: 'End Call & Save'
        }
      },
      requiresTextInput: false,
      step: 'condition', // Add step property for objection responses
      aiConfig: {
        canGenerateCustomScript: false,
        canAddCustomResponses: false,
        canSkipToAnyNode: false,
        contextWindow: 10,
        fallbackNode: 'call_end'
      }
    },

    'call_end': {
      id: 'call_end',
      script: 'Call has ended. Please fill out the prospect data form below.',
      coaching: 'Call concluded. Review lead categorization and follow-up actions.',
      responses: {},
      requiresTextInput: false,
      step: 'condition', // Add step property for objection responses
      aiConfig: {
        canGenerateCustomScript: false,
        canAddCustomResponses: false,
        canSkipToAnyNode: false,
        contextWindow: 25,
        fallbackNode: null
      }
    }
  }
};

// Script engine for handling node transitions
export class ScriptEngine {
  constructor(graph) {
    this.graph = graph;
  }

  getNode(nodeId) {
    return this.graph.nodes[nodeId];
  }

  getNextNode(currentNodeId, response, collectedData = {}) {
    console.log('🔍 ScriptEngine.getNextNode called:', { currentNodeId, response, collectedData });
    
    const node = this.getNode(currentNodeId);
    console.log('📝 Current node:', node);
    
    if (!node) {
      console.log('❌ Node not found:', currentNodeId);
      return null;
    }
    
    if (!node.responses[response]) {
      console.log('❌ Response not found in node:', { response, availableResponses: Object.keys(node.responses) });
      return null;
    }
    
    const nextNode = node.responses[response].nextNode;
    console.log('✅ Next node found:', nextNode);
    return nextNode;
  }

  getNodeConfig(nodeId, collectedData = {}) {
    // Handle special end script case
    if (nodeId === '__end_script__') {
      return {
        script: this.graph.endScriptConfig?.script || 'Thank you for your time. I\'ll now collect some basic information about your property to better assist you.',
        coaching: this.graph.endScriptConfig?.coaching || 'Keep it brief and professional. Transition smoothly to data collection.',
        buttons: [{
          label: 'Continue to Prospect Data Collection',
          value: 'end_call',
          nextStage: 'call_end',
          type: 'predetermined'
        }],
        requiresTextInput: false,
        isEndScript: true
      };
    }
    
    const node = this.getNode(nodeId);
    if (!node) {
      // Simple, user-friendly fallback for incomplete templates
      const availableNodes = Object.keys(this.graph.nodes || {});
      const openingNode = availableNodes.find(id => this.graph.nodes[id].isOpeningNode);
      const fallbackNode = openingNode || availableNodes[0];
      
      if (fallbackNode && fallbackNode !== nodeId) {
        // Prevent infinite recursion by checking if fallback is different
        console.log(`🔄 Redirecting from missing node "${nodeId}" to "${fallbackNode}"`);
        return this.getNodeConfig(fallbackNode, collectedData);
      } else {
        // If no nodes exist at all or we'd create recursion, end the script gracefully
        return {
          script: 'This template is still being built. Let\'s collect your information instead.',
          coaching: 'Template incomplete - ending gracefully.',
          buttons: [{
            label: 'Continue to Contact Information',
            value: 'end_call',
            nextStage: '__end_script__',
            type: 'predetermined'
          }],
          requiresTextInput: false
        };
      }
    }

    // Convert responses to button format for backward compatibility
    // But preserve the type field for open text responses
    const buttons = Object.entries(node.responses).map(([value, config]) => ({
      label: config.label,
      value: value,
      nextStage: config.nextNode,
      type: config.type || 'predetermined', // Preserve the type field
      dataField: config.dataField, // Preserve data field for open text
      placeholder: config.placeholder // Preserve placeholder for open text
    }));

    return {
      script: typeof node.script === 'function' ? node.script(collectedData) : node.script,
      coaching: node.coaching,
      buttons: buttons,
      requiresTextInput: node.requiresTextInput,
      aiConfig: node.aiConfig,
      step: node.step // Include the step property for objection responses
    };
  }

  // Simple check if script has basic structure
  hasBasicStructure() {
    const nodes = this.graph.nodes || {};
    const nodeIds = Object.keys(nodes);
    return nodeIds.length > 0;
  }

  // Future AI integration point
  async getNextNodeAI(currentNodeId, conversationHistory, collectedData, audioTranscript = null) {
    // This will be implemented in Phase 2
    console.log('AI integration not yet implemented');
    return this.getNextNode(currentNodeId, 'fallback', collectedData);
  }
} 